import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faCog } from "@fortawesome/free-solid-svg-icons";
import React, { useEffect, useState } from "react";
import { Dropdown, Nav, Tab } from "react-bootstrap";
import {
  Link,
  NavLink,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import MessagesIcon from "../../assets/icons/messages-01.svg2";
import GroupsIcon from "../../assets/icons/groups.svg2";
import { shortUUID } from "../helpers";
import JsonCollapse from "../Builder/Components/JsonCollapse";
import BroadcastEditor from "./BroadcastEditor";
import SaveAsTemplate from "./SaveAsTemplate";
import { proxyAPI } from "../proxyAPI";
import {
  selectSendListByUUID,
  setSendLists,
} from "../store/slices/sendListsSlice";
import { useDispatch, useSelector } from "react-redux";
import FullScreenLoadingIndicator from "../Common/FullScreenLoadingIndicator";
import { toast } from "react-toastify";
import BroadcastContacts from "./BroadcastContacts";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import BroadcastSendDropdown from "./BroadcastSendDropdown";
import BroadcastSendModals from "./BroadcastSendModals";
import BroadcastImportContacts from "./BroadcastImportContacts";
import BroadcastSettingsPage from "./BroadcastSettingsPage";
import { ImportedContactFileInfoType } from "./ImportContactsModal";
import {
  selectimportedFile,
  setCurrentBroadcastChannel,
  setUsedFile,
} from "../store/slices/broadcastSlice";
import {
  BroadcastType,
  selectBroadcast,
  setBroadcast,
  setNewBroadcast,
} from "../store/slices/broadcastSlice";
import LoadFromTemplateButton from "./LoadFromTemplateButton";
import { useGetCommsSettingsDataQuery } from "../store/slices/settingsSlice";
import HelpButton from "../HelpContent/HelpButton";
import { useConfirmDialog } from "../Builder/Components/ConfirmDialogContext";

export default function BroadcastPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const confirmDialog = useConfirmDialog() as
    | null
    | ((title: string, message: string) => Promise<boolean>);
  // if navigated from another page, get those contacts from location state
  const [locationContacts, setLocationContacts] = useState<{
    contacts: any[];
    contactSets: any[];
  }>();

  // get broadcast uuid from url
  const { broadcastPage, broadcastUUID } = useParams();
  const action = searchParams.get("action");
  const isTest = action === "send-test";

  // connection to redux
  const broadcastData = useSelector(selectBroadcast);
  const dispatch = useDispatch();
  const setBroadcastData = (v: BroadcastType) => dispatch(setBroadcast(v));

  const [loading, setLoading] = useState(false);

  // send list uuid state
  // default to broadcast-sendlist before it is saved
  // if file import is selected it will be set to broadcast-import-file
  const [sendListUUID, setSendListUUID] = useState("broadcast-sendlist");
  const importedFile = useSelector(selectimportedFile);

  const { data: commsSettings } = useGetCommsSettingsDataQuery();
  const [isBroadcastFixed, setIsBroadcastFixed] = useState(false);
  useEffect(() => {
    if (commsSettings) {
      setIsBroadcastFixed(commsSettings.IsBroadcastFixed);
    }
  }, [commsSettings]);

  const confirmFixedBroadcast = async (callBack: any) => {
    if (isBroadcastFixed) {
      if (
        confirmDialog &&
        !(await confirmDialog(
          "Use different settings",
          "This broadcast has fixed settings. Are you sure you want to use different settings in this broadcast?"
        ))
      ) {
        return;
      }
      setIsBroadcastFixed(false);
      callBack();
    } else {
      callBack();
    }
  };

  useEffect(() => {
    if (!commsSettings) return;

    (async () => {
      if (broadcastUUID === "new") {
        dispatch(setCurrentBroadcastChannel("sms"));
        if (location.state?.importedContactFile) {
          setSendListUUID("broadcast-import-file");
          dispatch(setUsedFile(location.state.importedContactFile));
          dispatch(
            setNewBroadcast({
              File: location.state.importedContactFile.file,
              IsFileImport: true,
              commsSettings,
            })
          );
          return;
        }
        dispatch(setNewBroadcast({ commsSettings }));
        return;
      }
      const data = await proxyAPI({
        prefix: "logic/workflows/api",
        url: "broadcasters/" + broadcastUUID,
      });
      if (data) {
        dispatch(setBroadcast(data));
        dispatch(setCurrentBroadcastChannel(data.Messages?.[0].Channel));
        if (data.IsFileImport) {
          setSendListUUID("broadcast-import-file");
        }
        if (data.SendList) {
          const sendList = await proxyAPI({
            prefix: "contacts/v1",
            url: `/send-list/${data.SendList}?with=set`,
          });
          if (sendList) {
            dispatch(setSendLists([sendList]));
            setSendListUUID(data.SendList);
          }
        }
      }
    })();
    setLocationContacts(location.state);
    return () => {
      dispatch(setSendLists([]));
      dispatch(setBroadcast(null));
    };
  }, [broadcastUUID, commsSettings]);

  const sl = useSelector(selectSendListByUUID(sendListUUID));
  const sendList = locationContacts ||
    sl || {
      add: [],
    };

  const update = async () => {
    setLoading(true);

    const slUuid =
      broadcastData?.SendList ||
      (sendList.contacts?.length > 0 || sendList.contactSets?.length > 0
        ? shortUUID()
        : "");

    const data = await proxyAPI({
      prefix: "logic/workflows/api",
      url: "broadcasters",
      method: "POST",
      body: {
        ...broadcastData,
        // ContactUuids: ["to-be-removed"],
        SendList: slUuid,
        Kind: "default",
      },
    });
    if (data) {
      toast.success("Template updated successfully.");
      if (slUuid) {
        const sendlistResponse = await proxyAPI({
          prefix: "contacts/v1",
          method: "POST",
          url: `/manage-send-list/${slUuid}`,
          body: {
            addContacts: sendList.contacts,
            removeContacts: sendList.ignoredContacts,
            addGroups: sendList.contactSets,
            removeGroups: sendList.ignoredContactSets,
          },
        });
        if (sendlistResponse) {
          toast.success("Template contacts updated successfully.");
        }
      }
    }

    setLoading(false);
  };

  const send = async (testNumber: string | null) => {
    setLoading(true);

    // get imported contact fields
    const FileImportHeader = [
      ...new Set(
        broadcastData?.BuilderConfiguration?.Tokens.filter(
          (t: any) => t.Tag === "imported_contact_field"
        ).map((t: any) => t.Value)
      ),
    ];

    const body = {
      ...broadcastData,

      ContactUuids: sendList.contacts,
      IgnoreContactUuids: sendList.ignoredContacts,
      Groups: sendList.contactSets,
      IgnoreGroups: sendList.ignoredContactSets,
      // if remove all is clicked
      SendList: sendList.clearStatic ? "" : sendListUUID,

      ...(sendListUUID === "broadcast-import-file"
        ? {
            File: importedFile?.file,
            IsFileImport: FileImportHeader.length > 0,
            FileImportHeader,
          }
        : {}),
      // Ignore everything if test send
      ...(testNumber
        ? {
            ContactUuids: [testNumber],
            Groups: [],
            SendList: "",
            File: "",
            IsFileImport: false,
          }
        : {}),
    };

    const data = await proxyAPI({
      prefix: "logic/workflows/api",
      url: "broadcasters/send",
      method: "POST",
      body,
    });
    setTimeout(() => {
      setLoading(false);
      if (data) {
        toast.success(data.message);
      }
    }, 200);
    return data;
  };

  const [showErrors, setShowErrors] = useState<"test" | boolean>(false);
  const handleCloseSendModals = () => {
    setSearchParams({});
    if (isTest) {
      navigate(`/library/broadcast/${broadcastUUID}/messages`);
    }
  };

  const handleShowSendErrors = ((sendType: "test" | undefined) => {
    if (
      errors.filter((e) =>
        sendType === "test" ? e.type !== "Message recipients" : true
      ).length > 0
    ) {
      toast.error("Please fix errors before sending.");
      setShowErrors(sendType || true);
    } else {
      setShowErrors(false);
    }

    if (
      errors.filter((e) => e.type === "Message section").length === 0 &&
      errors.filter(
        (e) => e.type === "Message recipients" && sendType !== "test"
      ).length > 0
    ) {
      navigate(`/library/broadcast/${broadcastUUID}/recipients`);
    }
    if (
      errors.filter((e) => e.type === "Message section").length > 0 &&
      errors.filter(
        (e) => e.type === "Message recipients" && sendType !== "test"
      ).length === 0
    ) {
      navigate(`/library/broadcast/${broadcastUUID}/messages`);
    }
  }) as any;
  // checking errors
  const errors: {
    type: "Message recipients" | "Message section";
    content: JSX.Element | string;
    channel?: string;
  }[] = [];
  // no-contacts
  if (
    (sendList.contacts?.length ?? 0) === 0 &&
    (sendList.contactSets?.length ?? 0) === 0 &&
    broadcastData?.SendList === "" &&
    !importedFile
  ) {
    errors.push({
      type: "Message recipients",
      content: (
        <span>
          No contacts selected.{" "}
          <Link to={`/library/broadcast/${broadcastUUID}/recipients`}>
            Please select contacts.
          </Link>
        </span>
      ),
    });
  }

  broadcastData?.Messages?.filter(
    (m) => m.Message.EditorState === null || m.Message.PlainText === ""
  ).forEach((m) =>
    errors.push({
      type: "Message section",
      channel: m.Channel,
      content: "Message content is missing for " + m.Channel,
    })
  );

  if (
    broadcastData?.Messages?.find((m) => m.Channel === "email")?.EmailSubject
      ?.PlainText === ""
  ) {
    errors.push({
      type: "Message section",
      channel: "email",
      content: "Empty email subject",
    });
  }

  if (
    broadcastData?.Handlers?.find(
      (h) =>
        h.Do === "forward" &&
        h.Message?.EditorState?.blocks?.map((b: any) => b.text).join("") === ""
    )
  ) {
    errors.push({
      type: "Message section",
      content: "Handler with forward action has empty message content",
    });
  }

  if (
    broadcastData?.Handlers?.find(
      (h) =>
        h.Do === "forward" &&
        h.ForwardMethod === "email" &&
        h.EmailSubject?.EditorState?.blocks
          ?.map((b: any) => b.text)
          .join("") === ""
    )
  ) {
    errors.push({
      type: "Message section",
      content: "Handler with forward action has empty email subject field",
    });
  }

  if (
    broadcastData?.Handlers?.find(
      (h) =>
        h.Do === "forward" &&
        h.ForwardMethod === "email" &&
        h.EmailFrom?.EditorState?.blocks?.map((b: any) => b.text).join("") ===
          ""
    )
  ) {
    errors.push({
      type: "Message section",
      content: "Handler with forward action has empty email from field",
    });
  }

  const [recipientTotal, setRecipientTotal] = useState(0);
  const visibleErrors = isTest
    ? errors.filter((err) => err.type !== "Message recipients")
    : errors;

  useEffect(() => {
    (async () => {
      const sendlistResponse = await proxyAPI({
        prefix: "contacts/v1",
        method: "POST",
        url: `find`,
        body: {
          filters: [],
          paginate: {
            order: "desc",
            order_by: "created_at",
            page: 0,
            perPage: 12,
          },
          fields: { uuids: [], cards: true, channels: true },
          sendListOptions: {
            uuids: [sendListUUID],
            ignoreContacts: sendList.ignoredContacts,
            ignoreSets: sendList.ignoredContactSets,
            contacts: sendList.contacts,
            sets: sendList.contactSets,
            ignore: false,
          },
        },
      });
      if (sendlistResponse) {
        setRecipientTotal(sendlistResponse.pagination.total);
      }
    })();
  }, [sendListUUID, broadcastData?.refresh]);

  return (
    broadcastData && (
      <>
        <FullScreenLoadingIndicator loading={loading} />
        <div
          className="bg-loop-reverse row mx-0 my-3 rounded-3 align-items-center position-relative"
          key={broadcastData.refresh}
        >
          <span className="position-absolute top-0 start-50 translate-middle rounded-pill text-white py-1 px-4 text-spaced fw-bold w-auto bg-colored text-xs">
            OVERVIEW
          </span>

          <div className="col-12 col-md-6 col-xxl-4 p-3">
            <h1 className="fw-bold">
              Broadcast
              <HelpButton helpKey="create-broadcast-overview" />
            </h1>
            <span className="text-secondary">
              Create a new broadcast using boomLogic.
            </span>
          </div>

          <div className="col-12 col-md-6 col-xxl-4 p-3">
            <h3 className="fw-bold text-xxl-center">{broadcastData.Label}</h3>
            <div className="row">
              {broadcastData.Uuid ? (
                <>
                  <div className="col-12 col-xxl-6 text-xxl-end">
                    <span className="fw-bold text-nowrap">Boomerang ref: </span>{" "}
                    {broadcastData.Uuid}
                  </div>
                  <div className="col-12 col-xxl-6">
                    <span className="fw-bold">Your ref:</span>{" "}
                    {broadcastData.Reference}
                    {broadcastPage !== "settings" && (
                      <Link
                        to={`/library/broadcast/${broadcastUUID}/settings`}
                        type="button"
                        className="btn btn small p-0 px-1 ms-2 btn-outline-secondary border-0"
                      >
                        <FontAwesomeIcon icon={faCog} />
                      </Link>
                    )}
                  </div>
                </>
              ) : (
                <div className="col-12 text-xxl-center text-break">
                  <span className="fw-bold">Your ref:</span>{" "}
                  {broadcastData.Reference}
                  {broadcastPage !== "settings" && (
                    <Link
                      to={`/library/broadcast/${broadcastUUID}/settings`}
                      type="button"
                      className="btn btn small p-0 px-1 ms-2 btn-outline-secondary border-0"
                    >
                      <FontAwesomeIcon icon={faCog} />
                    </Link>
                  )}
                </div>
              )}
            </div>
            <hr />
            <div className="text-secondary text-xxl-center text-break">
              {broadcastData.Description}
            </div>
          </div>

          {(broadcastPage === "messages" || broadcastPage === "recipients") && (
            <div className="col-12 col-xxl-4 p-3">
              <div className="d-flex justify-content-end">
                <span className="d-flex align-items-center bg-white rounded-3 px-3 py-2 mx-2">
                  <MessagesIcon width={30} />
                  <div className="border-end border-tertiary mx-2 p-1 align-self-stretch" />
                  <div className="text-start small p-2">
                    <h6 className="fw-bold mb-0 fs-3 text-center">
                      {broadcastData.Messages?.length ?? 0}
                    </h6>
                    <span className="small opacity-50">MESSAGES</span>
                  </div>
                </span>
                <span className="d-flex align-items-center bg-white rounded-3 px-3 py-2  mx-2">
                  <GroupsIcon width={30} />
                  <div className="border-end border-tertiary mx-2  p-1 align-self-stretch" />
                  <div className="text-start small p-2">
                    <h6 className="fw-bold mb-0 fs-3 text-center">
                      {importedFile
                        ? importedFile.stats?.rows ?? 0
                        : recipientTotal ?? 0}
                    </h6>
                    <span className="small opacity-50">RECIPIENTS</span>
                  </div>
                </span>
              </div>
            </div>
          )}
        </div>

        {showErrors &&
          errors.filter((e) =>
            showErrors === "test" ? e.type !== "Message recipients" : true
          ).length > 0 && (
            <div className="alert alert-danger">
              <ul className="mb-0">
                {errors
                  .filter((e) =>
                    showErrors === "test"
                      ? e.type !== "Message recipients"
                      : true
                  )
                  .map((e, i) => (
                    <li key={i}>{e.content}</li>
                  ))}
              </ul>
            </div>
          )}

        {broadcastPage === "settings" ? (
          <BroadcastSettingsPage
            confirmFixedBroadcast={confirmFixedBroadcast}
            update={update}
          />
        ) : (
          <div className="">
            <Tab.Container
              id="left-tabs-example"
              activeKey={broadcastPage || "messages"}
              mountOnEnter
              unmountOnExit
              // onSelect={(k) => navigate(`/library/broadcast/${k}`)}
            >
              <div className="d-flex flex-wrap-reverse justify-content-between">
                <Nav className="border-bottom mb-3 flex-nowrap">
                  <Nav.Item>
                    <Nav.Link
                      as={NavLink}
                      to={`/library/broadcast/${broadcastUUID}/messages`}
                      className="text-black border-purple-gradient border-5 border-underline"
                      active={broadcastPage === ""}
                    >
                      <div className="text-center px-3">
                        <MessagesIcon width={20} className="my-2" />
                        <div className="fw-bold">
                          BROADCAST MESSAGES
                          <HelpButton helpKey="broadcast-messages" />
                        </div>
                      </div>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      as={NavLink}
                      to={`/library/broadcast/${broadcastUUID}/recipients`}
                      className="text-black border-purple-gradient border-5 border-underline"
                    >
                      <div className="text-center">
                        <GroupsIcon width={20} className="my-2" />
                        <div className="fw-bold">
                          BROADCAST RECIPIENTS
                          <HelpButton helpKey="broadcast-recipients" />
                        </div>
                      </div>
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
                <div className="d-flex align-items-center">
                  <BroadcastSendDropdown
                    errors={errors}
                    update={update}
                    sendList={sendList}
                    broadcastUUID={broadcastUUID}
                    values={broadcastData}
                    showErrors={handleShowSendErrors}
                  />
                </div>
              </div>

              {/* EDITOR TABS */}
              <Tab.Content>
                <Tab.Pane eventKey="messages">
                  <BroadcastEditor
                    confirmFixedBroadcast={confirmFixedBroadcast}
                  />
                </Tab.Pane>
                <Tab.Pane eventKey="recipients">
                  <div>
                    <BroadcastContacts
                      key={sendListUUID}
                      uuid={sendListUUID}
                      setSendListUUID={setSendListUUID}
                      onSave={undefined}
                      uuidType={undefined}
                      initialSendlist={locationContacts}
                      setTotal={setRecipientTotal}
                      showFileImportOption
                      importedFile={importedFile}
                      setBroadcastData={setBroadcastData}
                      setFile={(f: ImportedContactFileInfoType | undefined) => {
                        dispatch(setUsedFile(f));
                        if (f) {
                          setBroadcastData({
                            ...broadcastData,
                            File: f.file,
                            IsFileImport: true,
                          });
                        } else {
                          setBroadcastData({
                            ...broadcastData,
                            File: "",
                            IsFileImport: false,
                          });
                        }
                      }}
                      headers={broadcastData.FileImportHeader}
                    />
                  </div>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>

            <div className="mx-auto my-5 d-flex justify-content-center">
              <Link
                to={`/library/broadcast/${broadcastUUID}/settings`}
                type="button"
                className="btn btn small bg-purple-gradient rounded-pill border-2 pe-4 fw-bold"
              >
                <span className="d-inline-block rounded-circle bg-body py-1 px-2 me-2">
                  <FontAwesomeIcon icon={faCog} />
                </span>
                Settings
              </Link>
              <div className="d-flex align-items-center">
                <BroadcastSendDropdown
                  errors={errors}
                  update={update}
                  sendList={sendList}
                  broadcastUUID={broadcastUUID}
                  values={broadcastData}
                  showErrors={handleShowSendErrors}
                />
              </div>{" "}
            </div>
          </div>
        )}
        {visibleErrors.length === 0 && (
          <BroadcastSendModals
            action={action}
            isTest={isTest}
            values={broadcastData}
            recipientTotal={recipientTotal}
            send={send}
            sendList={sendList}
            broadcastUUID={broadcastUUID}
            onHide={handleCloseSendModals}
          />
        )}
        <JsonCollapse json={broadcastData} />
      </>
    )
  );
}
